import React from 'react';
import PropTypes from 'prop-types';
import { LuBanknote, LuCalendarDays, LuCircleCheck, LuPencil, LuTrash2, LuUndo2 } from 'react-icons/lu';
import DetailPane from '../../../../components/data/DetailPane';
import useSalaryPaymentText from '../salaryPaymentText';
import { PAID_GRADIENT, PaidBadge, initialsOf } from './SalaryPaymentCell';

const SECTION_TITLE = 'tw-px-2 tw-pb-1.5 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400 dark:tw-text-slate-500';

const AmountRow = ({ label, value, strong, tone }) => (
  <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-3 tw-px-2 tw-py-1">
    <dt className="tw-min-w-0 tw-truncate tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{label}</dt>
    <dd className={`tw-shrink-0 tw-tabular-nums ${strong ? 'tw-text-base tw-font-semibold tw-text-slate-900 dark:tw-text-white' : 'tw-text-sm tw-font-medium'} ${tone === 'negative' ? 'tw-text-rose-600 dark:tw-text-rose-300' : ''} ${tone === 'positive' ? 'tw-text-emerald-700 dark:tw-text-emerald-300' : ''} ${!strong && !tone ? 'tw-text-slate-700 dark:tw-text-slate-200' : ''}`}>
      {value}
    </dd>
  </div>
);

AmountRow.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.string.isRequired, strong: PropTypes.bool, tone: PropTypes.string };

/**
 * The selected salary: pay or take back the chosen month's payment, edit,
 * delete; the month's payment, the amounts and the payment history.
 */
const SalaryPaymentDetailPane = ({ docked, height, salary, month, position, onPrev, onNext, onClose, onPay, onReverse, onEdit, onDelete }) => {
  const { label, formatMoney, formatDate, formatPeriod } = useSalaryPaymentText();
  const paneLabel = label('panel.label');

  if (!salary) return <DetailPane docked={docked} height={height} open={false} label={paneLabel} onClose={onClose} />;

  const name = `${salary.employeeName} ${salary.employeeSurname}`.trim() || '-';
  const payment = salary.payments?.[month];
  const history = Object.entries(salary.payments || {}).sort(([a], [b]) => b.localeCompare(a));
  const { view } = salary;

  return (
    <DetailPane
      docked={docked}
      height={height}
      open
      label={paneLabel}
      heading={label('panel.heading', { period: formatPeriod(month) })}
      position={position}
      contentKey={salary.id}
      identity={{
        avatar: initialsOf(salary),
        gradient: PAID_GRADIENT[Boolean(salary.isCurrentMonthPaid)],
        title: name,
        subtitle: label('panel.netMonthly', { amount: formatMoney(view.netSalary) }),
        badges: <PaidBadge paid={salary.isCurrentMonthPaid} />,
      }}
      actions={[
        salary.isCurrentMonthPaid
          ? { key: 'reverse', label: label('panel.reverse'), icon: LuUndo2, tint: 'amber', onClick: onReverse }
          : { key: 'pay', label: label('panel.pay'), icon: LuBanknote, tint: 'emerald', onClick: onPay },
        { key: 'edit', label: label('panel.edit'), icon: LuPencil, tint: 'blue', onClick: onEdit },
        { key: 'delete', label: label('panel.delete'), icon: LuTrash2, tint: 'rose', onClick: onDelete },
      ]}
      details={[
        { key: 'contract', icon: LuCalendarDays, label: label('columns.contractDate'), value: formatDate(salary.contractDate) || '-' },
      ]}
      onPrev={onPrev}
      onNext={onNext}
      onClose={onClose}
    >
      {payment && (
        <section aria-label={label('panel.thisMonth')} className="tw-mx-1 tw-rounded-2xl tw-border tw-border-emerald-500/20 tw-bg-emerald-500/[0.06] tw-py-2">
          <p className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-pb-1 tw-text-xs tw-font-semibold tw-text-emerald-700 dark:tw-text-emerald-300">
            <LuCircleCheck aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
            {label('panel.paidOn', { date: formatDate(payment.paidDate) })}
          </p>
          <dl className="tw-px-1">
            <AmountRow label={label('panel.paidAmount')} value={formatMoney(payment.amount)} strong />
            {Number(payment.additionalPayment) > 0 && <AmountRow label={label('bulk.columns.additionalPayment')} value={`+${formatMoney(payment.additionalPayment)}`} tone="positive" />}
            {Number(payment.additionalDeduction) > 0 && <AmountRow label={label('bulk.columns.additionalDeduction')} value={`−${formatMoney(payment.additionalDeduction)}`} tone="negative" />}
          </dl>
        </section>
      )}

      <section aria-label={label('panel.amounts')} className="tw-px-1">
        <p className={SECTION_TITLE}>{label('panel.amounts')}</p>
        <dl>
          <AmountRow label={label('columns.grossSalary')} value={formatMoney(view.grossSalary)} />
          <AmountRow label={label('columns.totalDeductions')} value={formatMoney(view.totalDeductions)} tone="negative" />
          <AmountRow label={label('columns.netSalary')} value={formatMoney(view.netSalary)} strong />
          <AmountRow label={label('columns.suggestedCost')} value={formatMoney(view.suggestedCost)} />
        </dl>
      </section>

      <section aria-label={label('panel.history')} className="tw-px-1">
        <p className={SECTION_TITLE}>{label('panel.history')} · {history.length}</p>
        {history.length === 0 ? (
          <p className="tw-px-2 tw-text-sm tw-italic tw-text-slate-400">{label('panel.noHistory')}</p>
        ) : (
          <ul className="tw-space-y-0.5">
            {history.map(([period, item]) => (
              <li key={period} className={`tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-xl tw-px-2.5 tw-py-1.5 ${period === month ? 'tw-bg-teal-500/[0.07]' : ''}`}>
                <span className="tw-min-w-0">
                  <span className="tw-block tw-truncate tw-text-sm tw-font-medium tw-text-slate-800 dark:tw-text-slate-100">{formatPeriod(period)}</span>
                  <span className="tw-block tw-text-[11px] tw-text-slate-500 dark:tw-text-slate-400">{formatDate(item?.paidDate)}</span>
                </span>
                <span className="tw-shrink-0 tw-text-sm tw-font-semibold tw-tabular-nums tw-text-slate-800 dark:tw-text-slate-100">{formatMoney(item?.amount)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </DetailPane>
  );
};

SalaryPaymentDetailPane.propTypes = {
  docked: PropTypes.bool,
  height: PropTypes.number,
  salary: PropTypes.object,
  month: PropTypes.string.isRequired,
  position: PropTypes.shape({ index: PropTypes.number, total: PropTypes.number }),
  onPrev: PropTypes.func,
  onNext: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onPay: PropTypes.func.isRequired,
  onReverse: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default SalaryPaymentDetailPane;
